"use client";

import { useEffect } from "react";

/* Locks the document behind a dialog, lightbox or full-screen overlay.

   ⚠ `overflow: hidden` ALONE DOES NOT WORK ON iOS SAFARI. Touch scrolling
   ignores it entirely: the page keeps scrolling and rubber-banding behind the
   panel. `position: fixed` is what actually stops it, and it needs
   `top: -scrollY` to hold the page still — fixing the body alone teleports the
   reader to the top of the page.

   Used by the chat panel (components/Chatbot.tsx). The restore path is the fiddly half: `position: fixed` resets
   the scroll offset, so the cleanup has to put it back by hand.

   ⚠ THE SCROLLBAR GAP IS NOT AN ALTERNATIVE TO ANY OF THAT — it is the desktop
   half of the same fix. Removing the scrollbar narrows the viewport and the page
   behind jumps sideways, so its width is paid back as padding. On iOS there is
   no persistent scrollbar, `gap` is 0, and this is a no-op. Both halves ship
   together.

   Pass `false` to leave the page alone (a closed dialog), which is why the
   argument exists at all: hooks cannot be called conditionally. */
/* Where to put the document when a lock lets go.

   ⚠ NOT ALWAYS BACK WHERE IT WAS. A lock that is released AFTER a route change
   would otherwise drop the reader onto the NEW page at the OLD page's offset —
   the "opens halfway down" bug on phones. The chat panel stays open through
   NAV_CLOSE_DELAY while its navigation commits, and the App Router's own reset
   to the top runs against a position:fixed body, where it moves nothing; the
   cleanup then scrolled the new page to wherever the old one had been. So the
   restore is keyed on the path the lock was taken on: same page, restore;
   different page, start at the top — unless the URL carries a #hash, which the
   page's own anchor handling owns. */
export function releaseScroll(scrollY: number, lockedPath: string) {
  if (window.location.pathname === lockedPath) {
    window.scrollTo({ top: scrollY, behavior: "instant" });
  } else if (!window.location.hash) {
    window.scrollTo({ top: 0, behavior: "instant" });
  }
}

export function useBodyScrollLock(locked: boolean, media?: string) {
  useEffect(() => {
    if (!locked) return;
    /* `media` narrows the lock to viewports that actually need it — the chat
       panel is only a full-screen layer under 640px, and locking the document
       behind a desktop popover that does not cover it would be wrong. Evaluated
       once per lock rather than subscribed: a viewport that crosses the
       breakpoint while a dialog is open is not a case worth a listener, and the
       cleanup below restores whatever was found either way. */
    if (media && !window.matchMedia(media).matches) return;

    const { body } = document;

    /* ⚠ LOCKS NEST, AND THE INNER ONE MUST NOT RE-READ THE SCROLL POSITION.
       If another dialog already holds a lock when this one opens, a second
       lock runs while the first is still held. By then `position: fixed` has already
       taken the document out of flow and `window.scrollY` reads 0, not where
       the reader actually is. Trusting it would rewrite `top` to `-0px` and
       scroll the page to the top behind the open dialog.

       The outer lock's own `top` is the truth in that case, so an already-fixed
       body means: leave the offset alone and let the outer lock own it. Each
       level still saves and restores the exact styles it found, so unwinding in
       any order lands back on the state below it. */
    const nested = body.style.position === "fixed";
    const scrollY = nested ? 0 : window.scrollY;
    const lockedPath = window.location.pathname;
    const gap = window.innerWidth - document.documentElement.clientWidth;

    const prev = {
      overflow: body.style.overflow,
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      paddingRight: body.style.paddingRight,
    };

    body.style.overflow = "hidden";
    body.style.position = "fixed";
    /* Nested: the outer lock's offset is already correct — see above. */
    if (!nested) body.style.top = `-${scrollY}px`;
    body.style.left = "0";
    body.style.right = "0";
    /* Nested: the scrollbar is already gone and its width already paid back, so
       measuring again reads a gap of 0 and adding it twice would double-pad. */
    if (!nested && gap > 0) body.style.paddingRight = `${gap}px`;

    return () => {
      body.style.overflow = prev.overflow;
      body.style.position = prev.position;
      body.style.top = prev.top;
      body.style.left = prev.left;
      body.style.right = prev.right;
      body.style.paddingRight = prev.paddingRight;
      /* `position: fixed` dropped the scroll position on the way in — restore
         it, or closing the dialog dumps the reader at the top of the page.
         `instant` because this is a restoration, not a navigation: a smooth
         scroll here would animate the page back into place in full view.

         Only the OUTERMOST lock does this. A nested one is handing the document
         back to a lock that is still holding it fixed, where the scroll offset
         is not live and scrolling to 0 would be both meaningless and wrong. */
      if (!nested) releaseScroll(scrollY, lockedPath);
    };
  }, [locked, media]);
}
